import React, { useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route } from 'react-router-dom';
import { AuthProvider } from '@/contexts/FirebaseAuthContext';
import { AdminAuthProvider } from '@/contexts/AdminAuthContext';
import { Toaster } from '@/components/ui/toaster';
import HomePage from '@/pages/HomePage';
import ArtistPage from '@/pages/ArtistPage';
import LoginPage from '@/pages/LoginPage';
import SignupPage from '@/pages/SignupPage';
import AdminLoginPage from '@/pages/AdminLoginPage';
import AdminConsolePage from '@/pages/AdminConsolePage';
import AgentLandingPage from '@/pages/agent/AgentLandingPage';
import AgentSignupPage from '@/pages/agent/AgentSignupPage';
import AgentDashboardPage from '@/pages/agent/AgentDashboardPage';
import KaraokeLandingPage from '@/pages/KaraokeLandingPage';
import KaraokeSignupPage from '@/pages/KaraokeSignupPage';
import KaraokeLoginPage from '@/pages/KaraokeLoginPage';
import AdminDashboard from '@/pages/AdminDashboard';
import ProfilePage from '@/pages/ProfilePage';
import SetlistPage from '@/pages/SetlistPage';
import CalendarPage from '@/pages/CalendarPage';
import LiveDashboardPage from '@/pages/LiveDashboardPage';
import KaraokeDashboardPage from '@/pages/karaoke/KaraokeDashboardPage';
// import KaraokeLiveDashboardPage from '@/pages/KaraokeLiveDashboardPage';
import KaraokeTVDisplayPage from '@/pages/karaoke/KaraokeTVDisplayPage';
import KaraokePublicPage from '@/pages/karaoke/KaraokePublicPage';
import PayoutsPage from '@/pages/PayoutsPage';
import CreativeAssetsPage from '@/pages/CreativeAssetsPage';
import FindPerformerPage from '@/pages/FindPerformerPage';
import { ProtectedRoute, KaraokeProtectedRoute } from '@/features/shared/components/ProtectedRoute';
import KaraokeTVControlPanel from '@/pages/karaoke/KaraokeTVControlPanel';
import KaraokeAdminDashboard from '@/pages/karaoke/KaraokeAdminDashboard';
import MobileErrorBoundary from '@/features/shared/components/MobileErrorBoundary';
// Stripe onboarding pages removed - Trust Payments does not require onboarding
// import StripeConnectOnboardingPage from '@/pages/stripe/StripeConnectOnboardingPage';
// import StripeOnboardingSuccessPage from '@/pages/stripe/StripeOnboardingSuccessPage';
// import StripeOnboardingRefreshPage from '@/pages/stripe/StripeOnboardingRefreshPage';
import DebugPage from '@/pages/DebugPage';
import CashoutPage from '@/features/shared/components/CashoutPage';
import StorePage from '@/pages/StorePage';
import RefundPolicyPage from '@/pages/legal/RefundPolicyPage';
import TermsOfServicePage from '@/pages/legal/TermsOfServicePage';
import PrivacyPolicyPage from '@/pages/legal/PrivacyPolicyPage';
import CookieConsentBanner from '@/components/CookieConsentBanner';
import { PaymentReturnHandler } from '@/components/PaymentReturnHandler';
import MelodyGlobalHost from '@/components/MelodyGlobalHost';


// Import context providers
import { ArtistThemeProvider } from '@/features/artist-mode/contexts/ArtistThemeContext';
import { ArtistLiveStatusProvider } from '@/features/artist-mode/contexts/ArtistLiveStatusContext';
import { SharedProvider } from '@/features/shared/contexts/SharedContext';
import { MobileProvider } from '@/features/shared/contexts/MobileContext';
import { CurrencyProvider } from '@/contexts/CurrencyContext';


function App() {
  // CRITICAL: Force HTTPS redirect to prevent mixed content errors
  useEffect(() => {
    if (typeof window !== 'undefined' && window.location.protocol === 'http:' && !window.location.hostname.includes('localhost')) {
      console.warn('⚠️ HTTP detected - redirecting to HTTPS to prevent mixed content errors');
      window.location.replace(window.location.href.replace('http://', 'https://'));
    }
  }, []);

  return (
    <Router>
      <AuthProvider>
        <AdminAuthProvider>
          <CurrencyProvider>
          <SharedProvider>
            <MobileProvider>
              <div className="min-h-screen bg-black text-white">
                {(() => {
                  // Global runtime error overlay for debugging Safari blank screen
                  try {
                    if (!window.__YSR_ERROR_OVERLAY_INSTALLED__) {
                      window.__YSR_ERROR_OVERLAY_INSTALLED__ = true;
                      const overlay = document.createElement('div');
                      overlay.style.cssText = 'position:fixed;z-index:99999;left:0;right:0;bottom:0;background:rgba(127,29,29,0.95);color:#fff;font-family:system-ui,-apple-system,Segoe UI,Roboto,Helvetica,Arial,sans-serif;font-size:12px;line-height:1.4;padding:8px 10px;display:none;max-height:45vh;overflow:auto;';
                      overlay.id = 'ysr-error-overlay';
                      document.body.appendChild(overlay);
                      const show = (msg) => {
                        overlay.style.display = 'block';
                        const p = document.createElement('div');
                        p.style.margin = '4px 0';
                        p.textContent = msg;
                        overlay.appendChild(p);
                      };
                      window.addEventListener('error', (e) => {
                        show(`[error] ${e.message} @ ${e.filename}:${e.lineno}`);
                      });
                      window.addEventListener('unhandledrejection', (e) => {
                        const msg = e.reason && (e.reason.message || e.reason.stack || String(e.reason));
                        show(`[promise] ${msg}`);
                      });
                      console.log('%cYSR error overlay active','color:#fbbf24');
                    }
                  } catch(_) {}
                  return null;
                })()}
                {(() => {
                  // Safe-mode and service worker reset for troubleshooting
                  try {
                    const params = new URLSearchParams(window.location.search);
                    const doReset = params.get('reset') === '1';
                    const safeMode = params.get('safe') === '1';
                    if (safeMode) {
                      document.body.style.backgroundColor = '#000';
                    }
                    if (doReset && 'serviceWorker' in navigator) {
                      navigator.serviceWorker.getRegistrations().then((regs) => {
                        regs.forEach((reg) => reg.unregister());
                      });
                      if (window.caches && caches.keys) {
                        caches.keys().then((keys) => keys.forEach((k) => caches.delete(k)));
                      }
                      // Remove query to prevent repeat resets on reload
                      const url = new URL(window.location.href);
                      url.searchParams.delete('reset');
                      window.history.replaceState({}, '', url.toString());
                    }
                  } catch (_) {}
                  return null;
                })()}
                <Routes>
                  <Route path="/" element={<HomePage />} />
                  <Route path="/login" element={<LoginPage />} />
                  <Route path="/signup" element={<SignupPage />} />
                  <Route path="/find-performer" element={<FindPerformerPage />} />
                  
                  {/* Agent Mode Routes */}
                  <Route path="/agent" element={<AgentLandingPage />} />
                  <Route path="/agency" element={<AgentLandingPage />} />
                  <Route path="/agent/signup" element={<AgentSignupPage />} />
                  <Route path="/agent/dashboard" element={
                    <ProtectedRoute>
                      <AgentDashboardPage />
                    </ProtectedRoute>
                  } />
                  
                  {/* Legal Pages */}
                  <Route path="/refund-policy" element={<RefundPolicyPage />} />
                  <Route path="/terms-of-service" element={<TermsOfServicePage />} />
                  <Route path="/privacy-policy" element={<PrivacyPolicyPage />} />
                  
                  {/* Admin Console Routes */}
                  <Route path="/admin-login" element={<AdminLoginPage />} />
                  <Route path="/admin-console" element={<AdminConsolePage />} />
                  
                  {/* Debug Console Route */}
                  <Route path="/debug" element={<DebugPage />} />
                  
                  {/* Karaoke Routes - Must be before catch-all routes */}
                  <Route path="/karaoke" element={<KaraokeLandingPage />} />
                  <Route path="/karaoke/signup" element={<KaraokeSignupPage />} />
                  <Route path="/karaoke/login" element={<KaraokeLoginPage />} />
                  
                  {/* Karaoke Admin Dashboard - Must come before /karaoke/:urlSlug routes */}
                  <Route path="/karaoke/admin/*" element={
                    <KaraokeProtectedRoute>
                      <MobileErrorBoundary>
                        <KaraokeAdminDashboard />
                      </MobileErrorBoundary>
                    </KaraokeProtectedRoute>
                  } />
                  
                  {/* Karaoke TV Display and Public Pages - Dynamic routes */}
                  <Route path="/karaoke/:urlSlug/tv" element={<MobileErrorBoundary><KaraokeTVDisplayPage /></MobileErrorBoundary>} />
                  <Route path="/karaoke/:urlSlug/tv-control" element={<MobileErrorBoundary><KaraokeTVControlPanel /></MobileErrorBoundary>} />
                  <Route path="/karaoke/:urlSlug/live/:sessionId" element={<MobileErrorBoundary><KaraokePublicPage /></MobileErrorBoundary>} />
                  <Route path="/karaoke/:urlSlug/" element={<MobileErrorBoundary><KaraokePublicPage /></MobileErrorBoundary>} />
                  <Route path="/karaoke/:urlSlug" element={<MobileErrorBoundary><KaraokePublicPage /></MobileErrorBoundary>} />
                  
                  {/* Stripe Connect Onboarding Routes - REMOVED (Trust Payments does not require onboarding) */}
                  {/* <Route path="/onboarding" element={
                    <ProtectedRoute>
                      <ArtistThemeProvider>
                        <ArtistLiveStatusProvider>
                          <StripeConnectOnboardingPage />
                        </ArtistLiveStatusProvider>
                      </ArtistThemeProvider>
                    </ProtectedRoute>
                  } />
                  <Route path="/onboarding/success" element={<StripeOnboardingSuccessPage />} />
                  <Route path="/onboarding/refresh" element={<StripeOnboardingRefreshPage />} /> */}
                  
                  {/* Payouts Routes */}
                  <Route path="/cashout" element={
                    <ProtectedRoute>
                      <CashoutPage />
                    </ProtectedRoute>
                  } />
          
                  {/* Store Route - Now integrated into AdminDashboard */}
                  <Route path="/admin/store" element={
                    <ProtectedRoute>
                      <MobileErrorBoundary>
                        <ArtistThemeProvider>
                          <ArtistLiveStatusProvider>
                            <AdminDashboard />
                          </ArtistLiveStatusProvider>
                        </ArtistThemeProvider>
                      </MobileErrorBoundary>
                    </ProtectedRoute>
                  } />
                  
                  {/* Artist Mode Admin Dashboard */}
                  <Route path="/admin/*" element={
                    <ProtectedRoute>
                      <MobileErrorBoundary>
                        <ArtistThemeProvider>
                          <ArtistLiveStatusProvider>
                            <AdminDashboard />
                          </ArtistLiveStatusProvider>
                        </ArtistThemeProvider>
                      </MobileErrorBoundary>
                    </ProtectedRoute>
                  } />
                  
                  {/* Artist public pages - handle both with and without trailing slash */}
                  {/* IMPORTANT: These must come AFTER all /karaoke routes to prevent conflicts */}
                  <Route path="/:artistUrlSlug/" element={
                    <MobileErrorBoundary>
                      <ArtistPage />
                    </MobileErrorBoundary>
                  } />
                  <Route path="/:artistUrlSlug" element={
                    <MobileErrorBoundary>
                      <ArtistPage />
                    </MobileErrorBoundary>
                  } />
                  
                  {/* Catch-all route - redirect to homepage for any unmatched routes */}
                  <Route path="*" element={<HomePage />} />
                </Routes>
                <PaymentReturnHandler />
                <MelodyGlobalHost />
                <Toaster />
                <CookieConsentBanner />
              </div>
            </MobileProvider>
          </SharedProvider>
          </CurrencyProvider>
        </AdminAuthProvider>
      </AuthProvider>
    </Router>
  );
}

export default App;